import { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactCountryFlag from "react-country-flag";
import PropTypes from "prop-types";
import logo from "/images/logo.svg";

const NavLink = memo(({ to, children, isActive, onClick }) => {
  return isActive ? (
    <span className="relative text-white font-bold cursor-default">
      {children}
      <div className="absolute left-0 right-0 h-0.5 bg-linear-to-r from-[#9961a9] via-[#7a6db2] to-[#009fd6] bottom-[-4px]" />
    </span>
  ) : (
    <Link
      to={to}
      className="relative text-white hover:text-[#009fd6] transition-colors"
      onClick={onClick}
    >
      {children}
    </Link>
  );
});

NavLink.displayName = "NavLink";

NavLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const MenuIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    className="w-6 h-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    className="w-6 h-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const DropdownIcon = ({ isOpen }) => (
  <svg
    className={`w-4 h-4 ml-1 text-white transform transition-transform ${
      isOpen ? "rotate-180" : ""
    }`}
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path
      fillRule="evenodd"
      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

DropdownIcon.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia(query);
    const handleChange = (e) => setMatches(e.matches);

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [query]);

  return matches;
}

const LanguageSelector = memo(({ currentLanguage, onChange }) => {
  const { t } = useTranslation();
  const languages = useMemo(
    () => [
      { code: "en", name: "English", flag: "GB" },
      { code: "bg", name: "Български", flag: "BG" },
    ],
    []
  );

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLanguageChange = useCallback(
    async (code) => {
      try {
        await onChange(code);
        setDropdownOpen(false);
      } catch (error) {
        console.error("Language change failed:", error);
      }
    },
    [onChange]
  );

  const selectedLanguage = useMemo(
    () => languages.find((lang) => lang.code === currentLanguage),
    [languages, currentLanguage]
  );

  return (
    <>
      {/* Desktop Dropdown */}
      <div className="hidden lg:block relative text-left" ref={dropdownRef}>
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="flex items-center focus:outline-hidden px-2 py-1 rounded-xs text-white cursor-pointer"
          aria-label={t("common.selectLanguage")}
        >
          <ReactCountryFlag
            countryCode={selectedLanguage.flag}
            svg
            className="w-5 h-5"
            aria-hidden="true"
          />
          <span className="ml-1">{selectedLanguage.code.toUpperCase()}</span>
          <DropdownIcon isOpen={dropdownOpen} />
        </button>

        {dropdownOpen && (
          <div className="origin-top-right absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-black ring-1 ring-black/5 z-10">
            <div className="py-1" role="menu">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`flex items-center w-full px-2 py-1 text-sm cursor-pointer ${
                    lang.code === currentLanguage
                      ? "bg-gray-800 text-white"
                      : "text-gray-300 hover:bg-gray-800 hover:text-white"
                  }`}
                  role="menuitem"
                >
                  <ReactCountryFlag
                    countryCode={lang.flag}
                    svg
                    className="w-5 h-5"
                    aria-hidden="true"
                  />
                  <span className="ml-2">{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Inline Language Selector */}
      <div className="lg:hidden flex items-center space-x-2">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`flex items-center px-2 py-1 rounded-xs ${
              lang.code === currentLanguage
                ? "bg-gray-800 text-white"
                : "text-gray-300 hover:bg-gray-800 hover:text-white"
            }`}
            aria-label={`Switch to ${lang.name}`}
          >
            <ReactCountryFlag
              countryCode={lang.flag}
              svg
              className="w-5 h-5"
              aria-hidden="true"
            />
            <span className="ml-1">{lang.code.toUpperCase()}</span>
          </button>
        ))}
      </div>
    </>
  );
});

LanguageSelector.displayName = "LanguageSelector";

LanguageSelector.propTypes = {
  currentLanguage: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

function Navbar() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const location = useLocation();

  const isLandscapeMode = useMediaQuery(
    "(orientation: landscape) and (max-width: 64rem)"
  );

  const navbarData = useMemo(
    () => t("components.shared.navbar", { returnObjects: true }),
    [t]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const storedLang =
      typeof window !== "undefined"
        ? localStorage.getItem("selectedLanguage")
        : "bg";

    if (storedLang && storedLang !== i18n.language) {
      i18n.changeLanguage(storedLang);
    }

    const handleLanguageChange = (lng) => {
      setCurrentLanguage(lng);
      document.documentElement.lang = lng;
      typeof window !== "undefined" &&
        localStorage.setItem("selectedLanguage", lng);
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => i18n.off("languageChanged", handleLanguageChange);
  }, [i18n]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleOrientationChange = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener("orientationchange", handleOrientationChange);
    return () => {
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, [isOpen]);

  const handleLanguageChange = useCallback(
    async (code) => {
      return i18n.changeLanguage(code);
    },
    [i18n]
  );

  const closeMenu = useCallback(() => setIsOpen(false), []);

  const isActiveLink = useCallback(
    (path) => location.pathname === path,
    [location.pathname]
  );

  return (
    <nav
      className={`fixed w-full top-0 left-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-black/80 backdrop-blur-xs" : "bg-black"
      }`}
      aria-label="Main navigation"
      id="safePad"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {isActiveLink("/") ? (
            <div className="flex items-center gap-3 cursor-default">
              <img
                src={logo}
                alt={navbarData.logoAlt}
                className="h-12 w-auto"
              />
              <span className="text-gray-100 text-xl font-semibold leading-none">
                <span className="text-transparent bg-clip-text bg-linear-to-r from-[#e1393e] via-[#fd6e4c] to-[#ffd450]">
                  {navbarData.brand}
                </span>
              </span>
            </div>
          ) : (
            <Link
              to="/"
              className="flex items-center gap-3"
              onClick={closeMenu}
            >
              <img
                src={logo}
                alt={navbarData.logoAlt}
                className="h-12 w-auto"
              />
              <span className="text-gray-100 text-xl font-semibold leading-none">
                <span className="text-transparent bg-clip-text bg-linear-to-r from-[#e1393e] via-[#fd6e4c] to-[#ffd450]">
                  {navbarData.brand}
                </span>
              </span>
            </Link>
          )}

          <div className="hidden lg:flex items-center gap-8">
            <NavLink to="/" isActive={isActiveLink("/")} onClick={closeMenu}>
              {navbarData.menu.home}
            </NavLink>
            <NavLink
              to="/about"
              isActive={isActiveLink("/about")}
              onClick={closeMenu}
            >
              {navbarData.menu.about}
            </NavLink>
            <NavLink
              to="/solution"
              isActive={isActiveLink("/solution")}
              onClick={closeMenu}
            >
              {navbarData.menu.solution}
            </NavLink>
            <NavLink
              to="/pvt"
              isActive={isActiveLink("/pvt")}
              onClick={closeMenu}
            >
              {navbarData.menu.pvt}
            </NavLink>
            <NavLink
              to="/blog"
              isActive={isActiveLink("/blog")}
              onClick={closeMenu}
            >
              {navbarData.menu.blog}
            </NavLink>
            {isActiveLink("/contact") ? (
              <span className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-full cursor-default">
                {navbarData.menu.contact}
              </span>
            ) : (
              <Link
                to="/contact"
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-full transition"
                onClick={closeMenu}
              >
                {navbarData.menu.contact}
              </Link>
            )}
            <LanguageSelector
              currentLanguage={currentLanguage}
              onChange={handleLanguageChange}
            />
          </div>

          <button
            className="lg:hidden text-white"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? t("common.closeMenu") : t("common.openMenu")}
          >
            {isOpen ? (
              isLandscapeMode ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              ) : (
                <CloseIcon />
              )
            ) : (
              <MenuIcon />
            )}
          </button>
        </div>
      </div>

      {isOpen && (
        <div
          className={`lg:hidden ${
            isLandscapeMode
              ? `landscape-menu ${isScrolled ? "bg-black/80 backdrop-blur-xs" : "bg-black"}`
              : "fixed inset-0 h-screen w-screen bg-black z-50 flex flex-col overflow-hidden"
          }`}
        >
          {!isLandscapeMode && (
            <div
              className="flex justify-between items-center px-6 py-4 bg-black"
              id="safePad"
            >
              <Link to="/" onClick={closeMenu}>
                <img
                  src={logo}
                  alt={navbarData.logoAlt}
                  className="h-12 w-auto"
                />
              </Link>
              <button onClick={closeMenu} aria-label={t("common.closeMenu")}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="w-8 h-8 text-white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          <div
            className={`${
              isLandscapeMode
                ? "flex flex-row justify-around items-center gap-x-2"
                : "flex-1 overflow-y-auto flex flex-col items-center gap-8 mt-16 "
            } px-6`}
          >
            <NavLink to="/" isActive={isActiveLink("/")} onClick={closeMenu}>
              {navbarData.menu.home}
            </NavLink>
            <NavLink
              to="/about"
              isActive={isActiveLink("/about")}
              onClick={closeMenu}
            >
              {navbarData.menu.about}
            </NavLink>
            <NavLink
              to="/solution"
              isActive={isActiveLink("/solution")}
              onClick={closeMenu}
            >
              {navbarData.menu.solution}
            </NavLink>
            <NavLink
              to="/pvt"
              isActive={isActiveLink("/pvt")}
              onClick={closeMenu}
            >
              {navbarData.menu.pvt}
            </NavLink>
            <NavLink
              to="/blog"
              isActive={isActiveLink("/blog")}
              onClick={closeMenu}
            >
              {navbarData.menu.blog}
            </NavLink>
            {isActiveLink("/contact") ? (
              <span className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-full cursor-default">
                {navbarData.menu.contact}
              </span>
            ) : (
              <Link
                to="/contact"
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-full hover:opacity-80"
                onClick={closeMenu}
              >
                {navbarData.menu.contact}
              </Link>
            )}
            <LanguageSelector
              currentLanguage={currentLanguage}
              onChange={handleLanguageChange}
            />
          </div>
        </div>
      )}
    </nav>
  );
}

export default memo(Navbar);
